import React, { useState } from 'react';
import { CheckOutlined, LoadingOutlined } from '@ant-design/icons';
import { useDispatch, useIntl } from 'umi';
import classNames from 'classnames';
import dayjs from 'dayjs';

import { F } from '@/utils/intl';
import PrettyDate from '@/components/PrettyDate';
import { LeadActionTag } from '@/components/ActionTag';
import TaskActions from '@/components/Tasks/TaskActions';
import TaskAssignees from '@/components/Tasks/TaskAssignees';
import { partialEditLeadTaskRequest } from '@/services/api/leadTask';
import { doTaskActionRequest } from '@/services/api/taskAutomation';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import { pressableProps } from '@/utils/dom';
import { runTaskUiConsequences } from '@/utils/taskUiConsequences';
import { getTaskLead, hasPermissionToEditTask, isTaskSnoozed } from '@/utils/task';

import styles from './TaskDesignRow.less';

export type TaskState = 'overdue' | 'open' | 'standby' | 'done' | 'retired';

export const taskState = (task: LeadTaskModel): TaskState => {
  // Lead-embedded task lists include retired tasks — never paint those as overdue
  if (task.retired_at) return 'retired';
  if (task.is_completed) return 'done';
  if (isTaskSnoozed(task)) return 'standby';
  if (task.due_date && dayjs(task.due_date).isBefore(dayjs(), 'day')) return 'overdue';
  return 'open';
};

type ITaskDesignRowProps = {
  task: LeadTaskModel;
  request: () => void;
  /** Popover variant: tighter paddings, no avatar stack */
  compact?: boolean;
  /** Skip the project link (when the row already lives in a lead context) */
  hideProject?: boolean;
  /** Called when the row navigates away (task drawer / lead drawer opened) —
   * popovers use it to close themselves */
  onOpen?: () => void;
  /** Non-interactive render for the task-automation preview: buttons show but
   * fire nothing, the checkbox and row-open are inert, no API calls. */
  preview?: boolean;
};

/** One flat card row per task — checkbox, title + due chip + project,
 * assignee avatars, first task action + ⋮ as one split control.
 * The tasks page, the header tasks popover and the calendar event popover
 * all render this same representation. */
const TaskDesignRow: React.FC<ITaskDesignRowProps> = ({ task, request, compact, hideProject, onOpen, preview }) => {
  const dispatch = useDispatch();
  const intl = useIntl();
  const [toggling, setToggling] = useState(false);
  const [running, setRunning] = useState(false);

  const state = taskState(task);
  // Preview forces the row into its "editable" look so the action buttons show,
  // but every handler below early-returns on preview so nothing actually fires.
  const canEdit = preview || hasPermissionToEditTask(task);
  const lead = getTaskLead(task);
  const primaryAction = canEdit && !task.is_completed ? task.actions?.[0] : undefined;
  // The authored button type; unset means the automation editor's default
  const primaryWidget = primaryAction ? (primaryAction.ui_widget || 'normal') : null;

  const toggleDone = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    if (preview || !canEdit || toggling) return;
    setToggling(true);
    partialEditLeadTaskRequest({
      params: { taskId: task.id },
      data: { is_completed: !task.is_completed },
    })
      // Keep the spinner up until the surface refetched — the row visibly
      // resolves (updates or leaves the tab) instead of flashing back
      .then(() => Promise.resolve(request()))
      .finally(() => setToggling(false));
  };

  const runPrimary = (e?: React.SyntheticEvent) => {
    e?.stopPropagation();
    if (preview || !primaryAction || running) return;
    setRunning(true);
    doTaskActionRequest({ params: { taskId: task.id, actionId: primaryAction.uuid } })
      .then(async () => {
        // open_* consequences: the UI opens the surface; popovers close via onOpen
        if (await runTaskUiConsequences(dispatch, task, primaryAction)) onOpen?.();
      })
      .finally(() => { setRunning(false); request(); });
  };

  const openDrawer = () => {
    if (preview) return;
    dispatch({ type: 'leadTasks/openTaskDetailsDrawer', payload: { task } });
    onOpen?.();
  };

  return (
    <div
      className={classNames(styles.row, {
        [styles.rowDone]: state === 'done',
        [styles.rowRetired]: state === 'retired',
        [styles.rowCompact]: compact,
      })}
      onClick={openDrawer}
      {...pressableProps(openDrawer)}
    >
      <span
        role="checkbox"
        aria-checked={state === 'done'}
        aria-busy={toggling}
        aria-label={intl.formatMessage({ id: 'pages.tasks.markDone', defaultMessage: 'Mark as done' })}
        aria-disabled={!canEdit}
        tabIndex={canEdit ? 0 : -1}
        className={classNames(styles.checkbox, {
          [styles.checkboxChecked]: state === 'done' && !toggling,
          [styles.checkboxToggling]: toggling,
          [styles.checkboxDisabled]: !canEdit,
        })}
        onClick={toggleDone}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleDone(e); }
        }}
      >
        {toggling ? (
          <LoadingOutlined className={styles.checkSpinner} spin />
        ) : state === 'done' && <CheckOutlined className={styles.checkIcon} />}
      </span>

      <div className={styles.main}>
        <span className={styles.title}>{task.title}</span>
        <span className={styles.subline}>
          <span className={classNames(styles.dueChip, styles[`due_${state}`])}>
            {state === 'retired' ? (
              <F id="pages.tasks.retired" defaultMessage="Retired" />
            ) : state === 'done' ? (
              <F id="pages.tasks.state.done" defaultMessage="Done" />
            ) : state === 'standby' ? (
              <>
                <F id="pages.tasks.state.snoozed" defaultMessage="Snoozed" />
                {task.snooze_until && <> · <PrettyDate date={task.snooze_until} /></>}
              </>
            ) : task.due_date ? (
              <PrettyDate date={task.due_date} />
            ) : (
              <F id="pages.tasks.state.noDue" defaultMessage="No due date" />
            )}
          </span>
          {/* The standard lead tag (avatar + name) — same representation as
              the drawer's fact box and every table */}
          {!hideProject && lead && (
            <span className={styles.projectTag} onClick={(e) => e.stopPropagation()}>
              <LeadActionTag lead={lead as any} onClick={() => onOpen?.()} />
            </span>
          )}
        </span>
      </div>

      {!compact && (
        <span className={styles.owners} onClick={(e) => e.stopPropagation()}>
          <TaskAssignees task={task} size="small" />
        </span>
      )}

      <div className={styles.actions} onClick={(e) => e.stopPropagation()}>
        <span
          className={classNames(
            styles.actionControl,
            primaryAction && styles.actionControlSplit,
            primaryWidget && styles[`split_${primaryWidget}`],
          )}
        >
          {primaryAction && (
            <span
              className={classNames(
                styles.primaryAction,
                primaryWidget && styles[`widget_${primaryWidget}`],
                { [styles.primaryActionLoading]: running },
              )}
              onClick={runPrimary}
              {...pressableProps(runPrimary)}
            >
              {primaryAction.button_label}
            </span>
          )}
          <TaskActions
            // The primary action already sits next to the ⋮ — don't list it again
            task={primaryAction ? { ...task, actions: (task.actions || []).slice(1) } : task}
            updateRequest={request}
            hideActionText
            size="small"
            allActionsInDropdown
            preview={preview}
          />
        </span>
      </div>
    </div>
  );
};

export default TaskDesignRow;
